import React, { FC, MouseEvent, useEffect, useMemo } from 'react';
import {
    Selection as SelectionModel,
    SelectionMovie,
} from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { AnalyticsSenderProvider, useAnalytics } from '@funtech-int/yango-shared';
import { InfinitePagination } from '@funtech-int/rival';

import * as Card from '@/shared/components/Card';
import { filmRoute } from '@/scenarios/movie/routes';
import { useMovieModal } from '@/shared/hooks/useMovieModal';
import { useProfileManager } from '@/shared/hooks/useProfileManager';
import { getBadgeType } from '@/shared/lib/analyticsBranded';
import { InfiniteGrid } from '@/shared/components/Grid/InfiniteGrid';
import { clientOnlyPage } from '@/shared/lib/clientOnly';
import { AutoOpenPaymentWidget } from '@/shared/components/AutoOpenPaymentWidget/AutoOpenPaymentWidget';
// TODO: отказаться от прямого импорта когда перейдем на @/scenarios/player https://st.yandex-team.ru/SAFT-8848
import { PlayerManager } from '@/scenarios/legacy-player/components/PlayerManager/PlayerManagerMain';

import { useSelectionQuery } from '../../hooks/useSelectionQuery';
import { SelectionTitle } from '../SelectionTitle/SelectionTitle';
import { createAnalyticsEvents } from '../../lib/createAnalyticsEvents';
import { SelectionPageHead } from '../PageHead/PageHead';

interface SelectionContainerProps {
    selection: SelectionModel;
    pagination: InfinitePagination;
    isWatchLaterSelection: boolean;
}

const SelectionContainer: FC<SelectionContainerProps> = ({
    selection,
    isWatchLaterSelection,
    pagination,
}) => {
    const analytics = useAnalytics(createAnalyticsEvents);
    const profileManager = useProfileManager();

    useEffect(() => {
        analytics.showed(selection.key);
    }, [analytics, selection.key]);

    const movieModal = useMovieModal();

    const { getKey, renderItem } = useMemo(() => {
        const cardAnalytics = analytics.createSelectionCardAnalyticsEvents(selection);

        return {
            getKey(item: SelectionMovie) {
                return item.contentId;
            },

            renderItem(item: SelectionMovie, index: number) {
                const badgeType = getBadgeType({
                    profileManager,
                    isPurchasable: Boolean(item.viewOption?.isPurchasable),
                });

                const handleShow = () => {
                    cardAnalytics.cardViewed(
                        index,
                        item.contentId,
                        item.title.toString(),
                        badgeType
                    );
                };

                const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
                    e.preventDefault();
                    cardAnalytics.cardClicked(
                        index,
                        item.contentId,
                        item.title.toString(),
                        badgeType
                    );
                    movieModal.open(item.contentId);
                };

                return (
                    <Card.Movie
                        movie={item}
                        location={filmRoute({ id: item.contentId })}
                        withPlannedToWatch={!isWatchLaterSelection}
                        onShow={handleShow}
                        onClick={handleClick}
                    />
                );
            },
        };
    }, [analytics, movieModal, profileManager, selection, isWatchLaterSelection]);

    return (
        <InfiniteGrid
            items={selection.items}
            getKey={getKey}
            renderItem={renderItem}
            pagination={pagination}
            title={<SelectionTitle id={selection.id} title={selection.title} />}
        />
    );
};

const SelectionContent = clientOnlyPage(
    ({ selection, isWatchLaterSelection, pagination }: SelectionContainerProps) => (
        <>
            <PlayerManager />
            <AnalyticsSenderProvider>
                <SelectionContainer
                    selection={selection}
                    isWatchLaterSelection={isWatchLaterSelection}
                    pagination={pagination}
                />
                <AutoOpenPaymentWidget />
            </AnalyticsSenderProvider>
        </>
    )
);

export const Selection: FC = () => {
    const { selection, isWatchLaterSelection, pagination } = useSelectionQuery();

    return (
        <>
            <SelectionPageHead selection={selection} />
            <SelectionContent
                selection={selection}
                isWatchLaterSelection={isWatchLaterSelection}
                pagination={pagination}
            />
        </>
    );
};

export default Selection;
